<script setup lang="ts">
/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  КАРКАС СКАНЕРА — намеренно НЕ `AppShell`
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Сканер живёт вне админского каркаса, и это не про эстетику. Устройство здесь
 * другое: телефон в одной руке, на входе в зал, вторая рука занята бейджем.
 * Сайдбар с полутора десятками разделов на таком экране съедает половину места
 * и добавляет полтора десятка мишеней, по которым можно промахнуться и уехать
 * с рабочего экрана посреди очереди.
 *
 * Поэтому здесь: узкая колонка, крупные элементы, одна ссылка «в админку» — и
 * ничего больше. Выход из сканера — осознанное действие, а не случайный тап.
 *
 * ⚠️ Авторизация та же самая. Это не второе приложение и не второй логин: те же
 * токены, тот же `AppShell`-независимый `RouterView`. Разделены только маршруты
 * и оформление.
 */
import { LayoutDashboard, LogOut, ScanLine, Volume2, VolumeX } from '@lucide/vue'
import { ref } from 'vue'
import { useRouter } from 'vue-router'

import ConfirmDialog from '@/components/common/ConfirmDialog.vue'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/composables/useAuth'
import { soundEnabled } from '@/lib/scanner/preferences'

const router = useRouter()
const { displayName, store } = useAuth()

const loggingOut = ref(false)

/**
 * Выключатель звука — как в прототипе `./scan`.
 *
 * Живёт в шапке, а не в настройках: его дёргают по обстановке в зале, а не
 * настраивают один раз. Состояние переживает перезагрузку (`localStorage`),
 * потому что зал за смену не меняется.
 *
 * Вибрация не выключается вместе со звуком: это независимый канал, и в шумном
 * помещении он остаётся единственным.
 */
const sound = ref(soundEnabled.get())

function toggleSound(): void {
  sound.value = !sound.value
  soundEnabled.set(sound.value)
}

/**
 * Уходим на `/login` в любом случае — как и в `UserMenu`. Запрос может не
 * дойти (сеть в зале), но локально мы уже вышли, и оставлять человека внутри
 * значит показывать ему экран, где каждый запрос молча падает.
 */
async function onLogout(): Promise<void> {
  loggingOut.value = true
  try {
    await store.logout()
  } catch {
    /* сервер не подтвердил выход — локально мы всё равно вышли */
  } finally {
    loggingOut.value = false
    await router.push({ name: 'login' })
  }
}
</script>

<template>
  <div class="flex min-h-svh flex-col bg-muted/30">
    <header class="sticky top-0 z-10 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div class="mx-auto flex h-14 w-full max-w-2xl items-center gap-2 px-4">
        <ScanLine class="size-5 shrink-0 text-primary" />
        <span class="font-semibold">Сканер</span>

        <span class="ml-auto truncate text-xs text-muted-foreground">
          {{ displayName }}
        </span>

        <!--
          Звук: `aria-pressed` вместо смены `aria-label` — состояние кнопки
          читается скринридером как переключатель, а не как две разные кнопки.
        -->
        <Button
          variant="ghost"
          size="icon"
          :aria-label="sound ? 'Выключить звук' : 'Включить звук'"
          :aria-pressed="sound"
          @click="toggleSound"
        >
          <component :is="sound ? Volume2 : VolumeX" />
        </Button>

        <!--
          Ссылка в админку, а не полноценная навигация: у оператора может не
          быть там ничего полезного, но он должен уметь уйти, не выходя из
          учётной записи.
        -->
        <Button
          variant="ghost"
          size="icon"
          aria-label="Перейти в панель"
          @click="router.push({ name: 'events' })"
        >
          <LayoutDashboard />
        </Button>
        <Button variant="ghost" size="icon" aria-label="Выйти" :disabled="loggingOut" @click="onLogout">
          <LogOut />
        </Button>
      </div>
    </header>

    <!--
      Узкая колонка: на телефоне это вся ширина, на планшете и ноутбуке контент
      не расползается на всю диагональ — сканер остаётся сканером.
    -->
    <main id="main-content" tabindex="-1" class="mx-auto w-full max-w-2xl flex-1 p-4 outline-none">
      <RouterView />
    </main>

    <!-- Диалоги подтверждения нужны и здесь: `AppShell` в этой ветке не смонтирован. -->
    <ConfirmDialog />
  </div>
</template>
